import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { ProjectGovernanceState, type ProjectGovernanceDto } from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatPrice } from '@/utils/formatPrice';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { useGovernanceMutation } from './useGovernanceMutation';

const BASELINE_EDITABLE: ProjectGovernanceState[] = [
  ProjectGovernanceState.Preparation,
  ProjectGovernanceState.AwaitingInitiationAuthorization,
  ProjectGovernanceState.Initiation,
  ProjectGovernanceState.AwaitingProjectAuthorization,
];

const numOrNull = (v: string) => (v === '' ? null : Number(v));

export function ChangeAuthorityForm({
  projectId,
  currency,
  governance,
}: {
  projectId: string;
  currency: string;
  governance: ProjectGovernanceDto;
}) {
  const { t } = useTranslation();
  const canEdit =
    governance.permissions.isBusinessLayer && BASELINE_EDITABLE.includes(governance.state);
  const schema = yup
    .object({
      pmChangeLimit: yup
        .string()
        .defined()
        .test('num', t('workspace.governance.changeAuthority.invalid'), (v) => !v || Number(v) >= 0),
      changeAuthorityLimit: yup
        .string()
        .defined()
        .test('num', t('workspace.governance.changeAuthority.invalid'), (v) => !v || Number(v) >= 0),
      changeBudget: yup
        .string()
        .defined()
        .test('num', t('workspace.governance.changeAuthority.invalid'), (v) => !v || Number(v) >= 0),
    })
    .required();
  const { register, handleSubmit, formState } = useForm({
    resolver: yupResolver(schema),
    values: {
      pmChangeLimit: governance.pmChangeLimit === null ? '' : String(governance.pmChangeLimit),
      changeAuthorityLimit:
        governance.changeAuthorityLimit === null ? '' : String(governance.changeAuthorityLimit),
      changeBudget: governance.changeBudget === null ? '' : String(governance.changeBudget),
    },
  });
  const mutation = useGovernanceMutation(projectId, (v: { pmChangeLimit: string; changeAuthorityLimit: string; changeBudget: string }) =>
    projectGovernanceApi.setChangeAuthority(projectId, {
      expectedVersion: governance.version,
      pmChangeLimit: numOrNull(v.pmChangeLimit),
      changeAuthorityLimit: numOrNull(v.changeAuthorityLimit),
      changeBudget: numOrNull(v.changeBudget),
    }),
  );

  if (!canEdit) {
    if (governance.pmChangeLimit === null && governance.changeAuthorityLimit === null && governance.changeBudget === null) {
      return null;
    }
    return (
      <p className="text-sm">
        <span className="text-muted-foreground">{t('workspace.governance.changeAuthority.title')}: </span>
        {t('workspace.governance.changeAuthority.summary', {
          pm: governance.pmChangeLimit === null ? '–' : formatPrice(governance.pmChangeLimit, currency),
          ca: governance.changeAuthorityLimit === null ? '–' : formatPrice(governance.changeAuthorityLimit, currency),
          budget: governance.changeBudget === null ? '–' : formatPrice(governance.changeBudget, currency),
          used:
            governance.changeBudgetUsed === null ? '–' : formatPrice(governance.changeBudgetUsed, currency),
        })}
      </p>
    );
  }

  return (
    <form
      className="space-y-2"
      onSubmit={handleSubmit((v) => mutation.mutate(v))}
    >
      <h5 className="text-sm font-medium">{t('workspace.governance.changeAuthority.title')}</h5>
      <p className="text-xs text-muted-foreground">{t('workspace.governance.changeAuthority.hint')}</p>
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor="pmChangeLimit">{t('workspace.governance.changeAuthority.pmLimit')}</Label>
          <Input id="pmChangeLimit" type="number" min={0} step="0.01" {...register('pmChangeLimit')} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="changeAuthorityLimit">{t('workspace.governance.changeAuthority.caLimit')}</Label>
          <Input id="changeAuthorityLimit" type="number" min={0} step="0.01" {...register('changeAuthorityLimit')} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="changeBudget">{t('workspace.governance.changeAuthority.budget')}</Label>
          <Input id="changeBudget" type="number" min={0} step="0.01" {...register('changeBudget')} />
        </div>
      </div>
      {(formState.errors.pmChangeLimit || formState.errors.changeAuthorityLimit || formState.errors.changeBudget) && (
        <p className="text-xs text-destructive">{t('workspace.governance.changeAuthority.invalid')}</p>
      )}
      <Button type="submit" size="sm" variant="outline" disabled={mutation.isPending}>
        {t('workspace.governance.changeAuthority.save')}
      </Button>
    </form>
  );
}
